import { useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreate,
  useNotify,
  SimpleForm,
  Create,
  useResourceContext,
  required,
  number,
  maxLength,
  useRefresh,
  minLength,
  TextInput,
  useTranslate,
  BooleanInput,
} from "react-admin";
import MuiDatePickerInput from "../../common/MuiDatePickerInput";
import SaveDeleteBtns from "../../saveDeleteBtns/SaveDeleteBtn";
import parserFormatNumInput from "../../../utils/parserFormatNumInput";

const decimalNumber = (value, allValues) => {
  if (/^\d+(\.\d{1,2})?$/.test(value)) {
    return undefined;
  }
  return "Must be a valid number or decimal value";
};

const greaterThan = (attr) => (value, allValues) => {
  if (!allValues[attr] || allValues[attr] === "0001-01-01T00:00:00Z")
    return undefined;
  if (value < allValues[attr]) {
    return "Must be greater than " + allValues[attr];
  }
  return undefined;
};
const lessThan = (attr) => (value, allValues) => {
  if (!allValues[attr] || allValues[attr] === "0001-01-01T00:00:00Z")
    return undefined;
  if (value > allValues[attr]) {
    return "Must be less than " + allValues[attr];
  }
  return undefined;
};

const validatePercentage = [required(), decimalNumber];
const validateQuantity = [required(), number()];
const validateName = [required(), maxLength(50)];
const validateStartDate = [
  required(),
  minLength(10),
  maxLength(10),
  lessThan("end_date"),
];
const validateEndDate = [
  required(),
  minLength(10),
  maxLength(10),
  greaterThan("start_date"),
];

export default function PromotionAddDialog({ open, setOpen }) {
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const t = useTranslate();
  const resource = useResourceContext(); // cuman nama resource

  const save = useCallback(
    async (values) => {
      try {
        // rubah ke number
        values.minimal = +values.minimal;
        // rubah ke number
        await create(
          resource,
          { data: values },
          {
            returnPromise: true,
            onSuccess: (data) => {
              notify("ra.notification.created", {
                messageArgs: { smart_count: 1 },
              });
              setOpen(false);
              refresh();
            },
          }
        );
      } catch (error) {
        if (error.body && error.body.errors) {
          // The shape of the returned validation errors must match the shape of the form
          return error.body.errors;
        }
      }
    },
    [create]
  );

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted={false}
      fullWidth={true}
      maxWidth="md"
    >
      <Create title={" "} sx={{ "& .RaCreate-main": { mt: 0 } }}>
        <SimpleForm
          warnWhenUnsavedChanges
          onSubmit={save}
          toolbar={<SaveDeleteBtns />}
          mode="onBlur"
          reValidateMode="onBlur"
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 0,
            }}
          >
            <DialogTitle>{t("mine.dialog.create_promotion")}</DialogTitle>
            <IconButton onClick={handleClose} sx={{ mr: "11px" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ alignSelf: "normal" }}>
            <TextInput
              variant="outlined"
              source="name"
              fullWidth
              isRequired
              resettable
              validate={validateName}
              label="mine.label.name"
            />

            <MuiDatePickerInput
              variant="outlined"
              source="start_date"
              fullWidth
              isRequired
              validate={validateStartDate}
              label="mine.label.start_date"
              maxDateSource="end_date"
            />
            <MuiDatePickerInput
              variant="outlined"
              source="end_date"
              fullWidth
              isRequired
              validate={validateEndDate}
              label="mine.label.end_date"
              minDateSource="start_date"
            />

            <TextInput
              variant="outlined"
              source="minimal"
              label="Minimal"
              fullWidth
              validate={validateQuantity}
              defaultValue=""
              {...parserFormatNumInput}
            />
            <TextInput
              variant="outlined"
              source="divider"
              label="mine.label.divider"
              fullWidth
              validate={validatePercentage}
              helperText={t("mine.message.divider_value")}
              defaultValue=""
              format={(val) => {
                return +val;
              }}
              {...parserFormatNumInput}
            />
            <BooleanInput
              variant="outlined"
              source="always_round_up"
              label="Always round up"
              fullWidth
              helperText="Berapapun decimalnya bulatkan keatas (113 / 1.15 = 98.26 dibulatkan menjadi 99 bayar, 14 gratis)"
            />
          </DialogContent>
        </SimpleForm>
      </Create>
    </Dialog>
  );
}
